import { Box, Typography } from "@mui/material";
import React, { ReactNode } from "react";

import LogoMark from "../../components/common/LogoMark";

export const BRAND = "Alpha Affinity";
export const PRODUCT = "Market Intelligence";

export const dateLine = (date: Date): string =>
  date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// The head of an exported page or image: the logo and the brand, and on the
// right the date it was made
export const BrandHeader: React.FC<{ date: Date }> = ({ date }) => (
  <Box
    component="header"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      pb: 1.5,
      borderBottom: 2,
      borderColor: "text.primary",
    }}
  >
    <LogoMark size={40} />
    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "1.0625rem",
          letterSpacing: "0.06em",
          lineHeight: 1.2,
          textTransform: "uppercase",
        }}
      >
        {BRAND}
      </Typography>
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "0.6875rem",
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {PRODUCT}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary">
      {dateLine(date)}
    </Typography>
  </Box>
);

// Settings of a view said in words, "Label: value · Label: value", for an
// exported image in place of the controls that set them
export const ExportFacts: React.FC<{
  facts: ReadonlyArray<readonly [string, string]>;
  "aria-label"?: string;
}> = ({ facts, "aria-label": label }) =>
  facts.length === 0 ? null : (
    <Typography variant="body2" color="text.secondary" aria-label={label}>
      {facts.map(([name, value], index) => (
        <React.Fragment key={index}>
          {index > 0 && " · "}
          <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
            {name}:
          </Box>{" "}
          {value}
        </React.Fragment>
      ))}
    </Typography>
  );

export interface ExportFrameProps {
  // What the image shows, e.g. "Partnerships"
  title: string;
  // The scope it was drawn for, as label and value
  filters?: ReadonlyArray<readonly [string, string]>;
  generatedAt: Date;
  children?: ReactNode;
}

// An exported image of a view: the brand, the view's name and the scope it
// was drawn for over it, and where it is from under it, so that the image
// reads on its own in a slide or a report
const ExportFrame: React.FC<ExportFrameProps> = ({
  title,
  filters = [],
  generatedAt,
  children,
}) => (
  <Box
    data-testid="export-frame"
    sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2.5 }}
  >
    <BrandHeader date={generatedAt} />
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <ExportFacts facts={filters} aria-label="Scope" />
      </Box>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column" }}>{children}</Box>
    <Typography
      component="footer"
      variant="caption"
      color="text.secondary"
      sx={{ borderTop: 1, borderColor: "divider", pt: 1 }}
    >
      {`Source: ${BRAND} ${PRODUCT} · ${dateLine(generatedAt)}`}
    </Typography>
  </Box>
);

export default ExportFrame;
